import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import PoolIcon from '@mui/icons-material/Pool';
import GroupsIcon from '@mui/icons-material/Groups';
import EventNoteIcon from '@mui/icons-material/EventNote';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import DoNotDisturbIcon from '@mui/icons-material/DoNotDisturb';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import swimService from '../../api/services/swimService';
import SwimmerContactsDialog, { meaningfulMedical } from './SwimmerContactsButton';
import { AttendanceStatus, CoachCalendarSession, RosterEntry, RosterResponse } from '../../types/swim';
import { useIsMobile } from '../../hooks/useIsMobile';

interface CoachOption {
  staff_id: string;
  staff_name: string;
}

interface Props {
  date: string; // YYYY-MM-DD
  session: CoachCalendarSession;
  coaches: CoachOption[];
  // The day-level broadcast workout (DailyPracticePlan). Surfaced as a
  // muted note so the coach can copy-down if useful; the session workout
  // overrides it for display on the parent agenda.
  dailyNote: string | null;
  onClose: () => void;
  onSaved: () => void;
}

function to12Hour(hhmm: string | null): string {
  if (!hhmm) return '';
  const [hStr, m] = hhmm.split(':');
  const h = Number(hStr);
  if (!Number.isFinite(h)) return hhmm;
  const period = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${m ?? '00'} ${period}`;
}

const STATUS_BUTTONS: { value: AttendanceStatus; letter: string; label: string }[] = [
  { value: 'present', letter: 'P', label: 'Present' },
  { value: 'late', letter: 'L', label: 'Late' },
  { value: 'absent', letter: 'A', label: 'Absent' },
  { value: 'excused', letter: 'E', label: 'Excused' },
  { value: 'makeup', letter: 'M', label: 'Makeup' },
];

/** Auto-fill for rows where the swimmer's intent is already known (parent RSVP'd
 * a skip, or the swimmer is at a meet). For a regular swimmer with no signal,
 * we leave the row UNMARKED — the coach hasn't seen them yet, so showing "P"
 * preselected (the old behavior) implied the swimmer had been counted before
 * they even arrived. null = no preselected status. */
function defaultStatusFor(entry: RosterEntry): AttendanceStatus | null {
  if (entry.at_meet) return 'excused';
  if (entry.rsvp_status === 'skip') return 'absent';
  return null;
}

const SessionDetailDialog: React.FC<Props> = ({ date, session, coaches, dailyNote, onClose, onSaved }) => {
  const isMobile = useIsMobile();
  const [workout, setWorkout] = useState<string>(session.workout ?? '');
  const [staffIds, setStaffIds] = useState<string[]>(
    Array.from(new Set((session.staff ?? []).map((a) => a.staff_id))),
  );
  const [roster, setRoster] = useState<RosterResponse | null>(null);
  const [loadingRoster, setLoadingRoster] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Attendance state: enrollment_id -> status. Pre-populated from existing
  // records when present, otherwise from RSVP-driven defaults. The original
  // snapshot is kept so we only POST rows the coach actually changed.
  const [attendance, setAttendance] = useState<Record<string, AttendanceStatus>>({});
  const [originalAttendance, setOriginalAttendance] = useState<Record<string, AttendanceStatus | undefined>>({});
  // Which roster card's contacts popup is open (one at a time). The whole card is
  // the tap target; the attendance toggles stopPropagation so they don't open it.
  const [openContactId, setOpenContactId] = useState<string | null>(null);

  useEffect(() => {
    setLoadingRoster(true);
    void (async () => {
      try {
        const [rosterData, attendanceData] = await Promise.all([
          swimService.getRoster(session.program_id, { date }),
          swimService.getSessionAttendance(session.id).catch(() => ({ data: [] })),
        ]);
        setRoster(rosterData);

        // Build original-attendance map from GET response. Shape:
        // { success, data: [{enrollment_id, status, ...}] }
        const existing: Record<string, AttendanceStatus> = {};
        const records = (attendanceData?.data ?? []) as Array<{
          enrollment_id: string;
          status: AttendanceStatus;
        }>;
        for (const r of records) existing[r.enrollment_id] = r.status;
        setOriginalAttendance(existing);

        // Initial in-dialog state = existing record OR signal-based auto-fill
        // (skip / at-meet). Regular rows stay UNSET so the coach has to mark
        // them — pre-checking Present made it look like the swimmer was
        // counted before they arrived.
        const init: Record<string, AttendanceStatus> = {};
        for (const entry of rosterData.data) {
          const existingStatus = existing[entry.enrollment_id];
          if (existingStatus) {
            init[entry.enrollment_id] = existingStatus;
            continue;
          }
          const auto = defaultStatusFor(entry);
          if (auto) init[entry.enrollment_id] = auto;
        }
        setAttendance(init);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to load roster');
      } finally {
        setLoadingRoster(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.program_id, session.id, date]);

  const originalStaffIds = useMemo(
    () => Array.from(new Set((session.staff ?? []).map((a) => a.staff_id))).sort(),
    [session.staff],
  );

  async function save() {
    setBusy(true);
    setError(null);
    try {
      // Persist workout if it changed
      const workoutChanged = (workout || '') !== (session.workout || '');
      if (workoutChanged) {
        await swimService.setSessionWorkout(session.id, workout.trim() || null);
      }
      // Persist staff if it changed
      const sortedStaff = [...staffIds].sort();
      const staffChanged =
        sortedStaff.length !== originalStaffIds.length ||
        sortedStaff.some((id, i) => id !== originalStaffIds[i]);
      if (staffChanged) {
        await swimService.setSessionStaff(
          session.id,
          sortedStaff.map((staff_id) => ({ staff_id, role: 'instructor' })),
        );
      }
      // Persist attendance rows that differ from the original snapshot. New
      // rows post too; the backend upserts so re-saving an unchanged row would
      // be a no-op, but skipping unchanged rows keeps the request slim.
      const changedAttendance = Object.entries(attendance)
        .filter(([enrollment_id, status]) => originalAttendance[enrollment_id] !== status)
        .map(([enrollment_id, status]) => ({ enrollment_id, status }));
      if (changedAttendance.length > 0) {
        await swimService.markAttendance(session.id, changedAttendance);
      }
      onSaved();
    } catch (e: unknown) {
      type ErrLike = { response?: { data?: { error?: string } }; message?: string };
      const err = e as ErrLike;
      setError(err?.response?.data?.error || err?.message || 'Save failed');
    } finally {
      setBusy(false);
    }
  }

  function setStatus(enrollmentId: string, status: AttendanceStatus | null) {
    // null = the coach tapped the already-selected toggle (a "deselect" gesture)
    // — accept it and clear the row back to unmarked. This is the undo path for
    // an accidental tap, paired with the v79 unmarked-by-default behavior. Save
    // skips rows with no status, so deselected rows are not persisted.
    setAttendance((prev) => {
      if (status === null) {
        if (!(enrollmentId in prev)) return prev;
        const { [enrollmentId]: _drop, ...rest } = prev;
        return rest;
      }
      return { ...prev, [enrollmentId]: status };
    });
  }

  const selectedCoaches = coaches.filter((c) => staffIds.includes(c.staff_id));
  const timeLabel = session.start_time
    ? `${to12Hour(session.start_time)}${session.end_time ? ` – ${to12Hour(session.end_time)}` : ''}`
    : null;

  const rosterEntries: RosterEntry[] = roster?.data ?? [];

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth fullScreen={isMobile}>
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1}>
          <PoolIcon color="primary" />
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="h6">{session.program_name ?? 'Session'}</Typography>
            <Typography variant="caption" color="text.secondary">
              {(session.target_audience || 'kids')}
              {timeLabel && ` • ${timeLabel}`}
              {session.is_cancelled && ' • cancelled'}
            </Typography>
          </Box>
          {roster?.expected_today != null && (
            <Chip
              icon={<GroupsIcon fontSize="small" />}
              label={`${roster.expected_today} expected`}
              color="primary"
              size="small"
              sx={{ flexShrink: 0 }}
            />
          )}
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        {/* Coaches */}
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Coaches assigned</Typography>
        <Autocomplete
          multiple
          options={coaches}
          getOptionLabel={(o) => o.staff_name}
          isOptionEqualToValue={(a, b) => a.staff_id === b.staff_id}
          value={selectedCoaches}
          onChange={(_, selected) => setStaffIds(selected.map((s) => s.staff_id))}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={selectedCoaches.length === 0 ? 'Assign coaches' : ''}
            />
          )}
        />

        <Divider sx={{ my: 3 }} />

        {/* Workout */}
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <EventNoteIcon fontSize="small" color="success" />
          <Typography variant="subtitle2">Workout</Typography>
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          What this group is doing today. Overrides the day-level note for {session.program_name}.
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={5}
          placeholder={`200 warm-up\n4x50 free on :60\n200 cool-down`}
          value={workout}
          onChange={(e) => setWorkout(e.target.value)}
        />
        {dailyNote && (
          <Box
            sx={(theme) => ({
              mt: 1.5,
              p: 1.25,
              borderRadius: 1,
              backgroundColor: alpha(theme.palette.info.main, 0.08),
            })}
          >
            <Typography variant="caption" color="text.secondary">
              Day-level note (broadcast to all classes):
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.25 }}>
              {dailyNote}
            </Typography>
          </Box>
        )}

        <Divider sx={{ my: 3 }} />

        {/* Today's roster + attendance */}
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <HowToRegIcon fontSize="small" color="primary" />
          <Typography variant="subtitle2">Attendance</Typography>
          {loadingRoster && <CircularProgress size={14} />}
          {!loadingRoster && Object.keys(originalAttendance).length > 0 && (
            <Chip
              size="small"
              variant="outlined"
              color="success"
              label={`${Object.keys(originalAttendance).length} previously recorded`}
            />
          )}
          <Box sx={{ flex: 1 }} />
          {!loadingRoster && rosterEntries.length > 0 && (
            <Button
              size="small"
              startIcon={<CheckCircleIcon fontSize="small" />}
              onClick={() => {
                const allPresent: Record<string, AttendanceStatus> = {};
                for (const e of rosterEntries) allPresent[e.enrollment_id] = 'present';
                setAttendance(allPresent);
              }}
            >
              Mark all present
            </Button>
          )}
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          P=Present · L=Late · A=Absent · E=Excused · M=Makeup
        </Typography>
        {!loadingRoster && rosterEntries.length === 0 && (
          <Alert severity="info">No swimmers enrolled in this class.</Alert>
        )}
        <Stack
          divider={<Divider />}
          sx={{
            border: rosterEntries.length ? '1px solid rgba(0,0,0,0.08)' : undefined,
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          {rosterEntries.map((e) => {
            const s = e.swimmer;
            // Filter N/A-style placeholders so a parent's "n/a" entry doesn't
            // fire the red Medical alert on every roster row (alert fatigue).
            const allergies = meaningfulMedical(s.allergies);
            const medicalConditions = meaningfulMedical(s.medical_conditions);
            const hasMedical = !!allergies || !!medicalConditions;
            const isSkip = e.rsvp_status === 'skip';
            const atMeet = e.at_meet;
            const rsvpHint = atMeet ? (
              <Chip
                icon={<EmojiEventsIcon fontSize="small" />}
                label="At meet"
                color="warning"
                size="small"
                variant="outlined"
              />
            ) : isSkip ? (
              <Chip
                icon={<DoNotDisturbIcon fontSize="small" />}
                label="Skipping"
                size="small"
                variant="outlined"
              />
            ) : null;
            const currentStatus = attendance[e.enrollment_id];
            return (
              <Box
                key={e.enrollment_id}
                onClick={() => setOpenContactId(e.enrollment_id)}
                onKeyDown={(ev) => {
                  if ((ev.key === 'Enter' || ev.key === ' ') && ev.target === ev.currentTarget) {
                    ev.preventDefault();
                    setOpenContactId(e.enrollment_id);
                  }
                }}
                role="button"
                tabIndex={0}
                aria-label={`Contact info for ${s.first_name} ${s.last_name}`}
                sx={{ p: 1.5, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
              >
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  justifyContent="space-between"
                  alignItems={{ xs: 'stretch', sm: 'flex-start' }}
                  spacing={1.5}
                >
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    {/* The whole card is the tap target — tapping it opens the
                        contacts popup (parent + emergency phone + medical). */}
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {`${s.first_name ?? ''} ${s.last_name ?? ''}`.trim() || '—'}
                        {s.age != null && (
                          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.75 }}>
                            age {s.age}
                          </Typography>
                        )}
                      </Typography>
                      {rsvpHint}
                    </Stack>
                    {/* Medical stays INLINE in the row (safety-critical — never
                        hide it behind a click). The contact-dialog also shows it. */}
                    {hasMedical && (
                      <Alert severity="error" icon={<WarningIcon fontSize="small" />} sx={{ mt: 0.75, py: 0.25 }}>
                        {allergies && <div><strong>Allergies:</strong> {allergies}</div>}
                        {medicalConditions && <div><strong>Medical:</strong> {medicalConditions}</div>}
                      </Alert>
                    )}
                  </Box>
                  <Box sx={{ flexShrink: 0 }} onClick={(ev) => ev.stopPropagation()}>
                    <ToggleButtonGroup
                      exclusive
                      size="small"
                      value={currentStatus ?? null}
                      onChange={(_, val: AttendanceStatus | null) => setStatus(e.enrollment_id, val)}
                      aria-label={`Attendance for ${s.first_name} ${s.last_name}`}
                    >
                      {STATUS_BUTTONS.map((opt) => (
                        <Tooltip key={opt.value} title={opt.label}>
                          <ToggleButton
                            value={opt.value}
                            aria-label={opt.label}
                            sx={{ px: 1.25, py: 0.25, fontWeight: 600, minWidth: 32 }}
                          >
                            {opt.letter}
                          </ToggleButton>
                        </Tooltip>
                      ))}
                    </ToggleButtonGroup>
                  </Box>
                </Stack>
                <SwimmerContactsDialog
                  open={openContactId === e.enrollment_id}
                  onClose={() => setOpenContactId(null)}
                  firstName={s.first_name}
                  lastName={s.last_name}
                  age={s.age}
                  contact={{
                    parent_name: s.parent_name,
                    parent_phone: s.parent_phone,
                    emergency_contact_name: s.emergency_contact_name,
                    emergency_contact_phone: s.emergency_contact_phone,
                    allergies: s.allergies,
                    medical_conditions: s.medical_conditions,
                  }}
                />
              </Box>
            );
          })}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button onClick={() => void save()} variant="contained" disabled={busy}>
          {busy ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SessionDetailDialog;
